import React, { useState, useEffect } from 'react';
import { Alert, Button, Card } from '@/components/ui';
import { Invoice } from '../../../types/Invoice';
import InvoiceList from './InvoiceList';
import InvoiceModal from './InvoiceModal';
import { projectService } from '../../../services/projectService';

interface InvoiceTabProps {
  projectId: number;
}

const InvoiceTab: React.FC<InvoiceTabProps> = ({ projectId }) => {
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [loading, setLoading] = useState(true);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingInvoice, setEditingInvoice] = useState<Invoice | undefined>();
  const [refreshing, setRefreshing] = useState(false);
  // Upload problems are reported inline above the list (STATE-06), not in a browser alert.
  const [uploadNotice, setUploadNotice] = useState<string | null>(null);

  useEffect(() => {
    if (projectId) {
      loadInvoices();
    }
  }, [projectId]);

  const loadInvoices = async () => {
    try {
      setLoading(true);
      const invoiceData = await projectService.getProjectInvoices(projectId);
      setInvoices(invoiceData);
    } catch (error) {
      console.error('Error loading invoices:', error);
      // Handle error appropriately
    } finally {
      setLoading(false);
    }
  };

  const handleAddInvoice = () => {
    setEditingInvoice(undefined);
    setIsModalVisible(true);
  };

  const handleEditInvoice = (invoice: Invoice) => {
    setEditingInvoice(invoice);
    setIsModalVisible(true);
  };

  const handleRetryUpload = async (invoiceId: number, attachmentId: number) => {
    // The original file isn't kept, so a failed upload can't be re-sent from here.
    setUploadNotice("This attachment didn't upload. To retry, delete the failed attachment and upload the file again – the file name is cleaned automatically.");
  };

  const handleSaveInvoice = async (invoiceData: any, file?: File) => {
    try {
      setRefreshing(true);
      
      if (editingInvoice) {
        // Update existing invoice
        await projectService.updateInvoice(projectId, editingInvoice.id, invoiceData);
      } else {
        // Create new invoice
        const savedInvoice = await projectService.createInvoice(projectId, invoiceData);
        
        // If there's a file, upload it as an attachment
        if (file && savedInvoice?.id) {
          try {
            console.log('Uploading file attachment for invoice:', savedInvoice.id);
            await projectService.uploadInvoiceAttachment(projectId, savedInvoice.id, file);
            console.log('File uploaded successfully');
          } catch (fileError) {
            console.error('Error uploading file:', fileError);
            // The invoice is saved; say the file isn't, above the list (STATE-06)
            setUploadNotice("Invoice created, but the file didn't upload. Upload it again from the invoice.");
          }
        }
      }
      
      await loadInvoices();
    } catch (error) {
      console.error('Error saving invoice:', error);
      throw error;
    } finally {
      setRefreshing(false);
    }
  };

  const handleDeleteInvoice = async (invoiceId: number) => {
    try {
      await projectService.deleteInvoice(projectId, invoiceId);
      await loadInvoices();
    } catch (error) {
      console.error('Error deleting invoice:', error);
      throw error;
    }
  };

  const handleViewAttachment = (attachment: any, invoice: Invoice) => {
    // Check if this is a placeholder URL (failed upload)
    if (attachment.attachmentURL && attachment.attachmentURL.startsWith('placeholder://')) {
      setUploadNotice(`"${attachment.attachmentName}" didn't upload, so it can't be opened. Upload the file again.`);
      return;
    }
    
    // Call backend endpoint to download the file (same pattern as reports)
    window.open(`/api/projects/${projectId}/invoices/${invoice.id}/attachments/${attachment.id}/download`, '_blank');
  };

  const handleCloseModal = () => {
    setIsModalVisible(false);
    setEditingInvoice(undefined);
  };

  const getTotalInvoiceValue = () => {
    return invoices.reduce((total, invoice) => total + invoice.invoiceValue, 0);
  };

  return (
    <div>
      <Card>
        <Card.Header className="tw-flex tw-justify-between tw-items-center">
          <h6 className="tw-mb-0">Project Invoices</h6>
          <div className="tw-flex tw-items-center tw-gap-3">
            <div className="tw-text-content-muted">
              Total: <span className="tw-font-bold">${getTotalInvoiceValue().toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>
            </div>
            <Button
              variant="primary"
              size="sm"
              onClick={handleAddInvoice}
              disabled={refreshing}
            >
              <i className="bi bi-plus-lg tw-mr-2"></i>
              Add Invoice
            </Button>
          </div>
        </Card.Header>
        <Card.Body>
          {uploadNotice && (
            <Alert variant="warning" dismissible onClose={() => setUploadNotice(null)} className="tw-mb-3">
              {uploadNotice}
            </Alert>
          )}
          <InvoiceList
            invoices={invoices}
            onEdit={handleEditInvoice}
            onDelete={handleDeleteInvoice}
            onViewAttachment={handleViewAttachment}
            onRetryUpload={handleRetryUpload}
            loading={loading}
          />
        </Card.Body>
      </Card>

      <InvoiceModal
        visible={isModalVisible}
        onClose={handleCloseModal}
        onSave={handleSaveInvoice}
        invoice={editingInvoice}
        projectId={projectId}
      />
    </div>
  );
};

export default InvoiceTab;
